import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check, CircleAlert, Shield } from 'lucide-react'
import { Alert, Avatar, Button, Spinner } from '@agion/ui'
import type { UserInfo } from '@agion/oidc-client'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import {
  parseAuthorizationRequest,
  checkSession,
  getSessionUser,
  getAppName,
  parseScopes,
  getScopeDescriptions,
  isValidRedirectUri,
  buildErrorResponse,
  storeAuthorizationRequest,
  type AuthorizationRequest
} from '@/lib/oidc'

export const AuthorizePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [_isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<UserInfo | null>(null)
  const [authRequest, setAuthRequest] = useState<AuthorizationRequest | null>(null)
  const [error, setError] = useState<string>('')
  const [isAuthorizing, setIsAuthorizing] = useState(false)

  const authorizeRequest = useCallback(async (request: AuthorizationRequest) => {
    try {
      setIsAuthorizing(true)
      setError('')

      // Call backend to create authorization code
      const response = await fetch('/oauth/authorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          client_id: request.client_id,
          redirect_uri: request.redirect_uri,
          scope: request.scope,
          state: request.state,
          code_challenge: request.code_challenge,
          code_challenge_method: request.code_challenge_method,
          response_type: request.response_type
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || t('authorize.authorizationFailed'))
      }

      const data = await response.json()

      if (!data.success || !data.redirect_url) {
        throw new Error(t('authorize.invalidServerResponse'))
      }

      // SECURITY: Validate that the redirect URL starts with the registered redirect_uri
      // This prevents the server from being tricked into redirecting to an attacker's site
      const redirectBase = new URL(request.redirect_uri).origin + new URL(request.redirect_uri).pathname
      if (!data.redirect_url.startsWith(redirectBase)) {
        throw new Error(t('authorize.redirectMismatch'))
      }

      // Redirect back to client with authorization code
      window.location.href = data.redirect_url
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('authorize.authorizationFailed')
      setError(errorMessage)

      // Redirect with error if we have a redirect URI
      const errorUrl = buildErrorResponse(
        request.redirect_uri,
        'server_error',
        errorMessage,
        request.state
      )
      setTimeout(() => {
        window.location.href = errorUrl
      }, 3000)
    } finally {
      setIsAuthorizing(false)
    }
  }, [t])

  useEffect(() => {
    const checkAuthAndParseRequest = async () => {
      try {
        // Parse authorization request from URL
        const searchParams = new URLSearchParams(window.location.search)
        const request = parseAuthorizationRequest(searchParams)

        if (!request) {
          setError(t('authorize.invalidRequest'))
          setIsLoading(false)
          return
        }

        // Validate redirect URI
        if (!isValidRedirectUri(request.redirect_uri, request.client_id)) {
          setError(t('authorize.invalidRedirectUri'))
          setIsLoading(false)
          return
        }

        // Only support authorization code flow
        if (request.response_type !== 'code') {
          const errorUrl = buildErrorResponse(
            request.redirect_uri,
            'unsupported_response_type',
            'Only authorization code flow is supported',
            request.state
          )
          window.location.href = errorUrl
          return
        }

        setAuthRequest(request)

        // Store request for after login
        storeAuthorizationRequest(request)

        // Check if user is already authenticated
        const authenticated = await checkSession()
        setIsAuthenticated(authenticated)

        if (authenticated) {
          const currentUser = await getSessionUser()
          setUser(currentUser)

          // If prompt=none, auto-authorize without consent screen
          if (request.prompt === 'none') {
            await authorizeRequest(request)
            return
          }
        } else {
          // Not authenticated, redirect to login with return_to
          const returnTo = window.location.pathname + window.location.search
          navigate(`/login?return_to=${encodeURIComponent(returnTo)}`)
          return
        }
      } catch (err) {
        console.error('Authorization check failed:', err)
        setError(t('authorize.processingError'))
      } finally {
        setIsLoading(false)
      }
    }

    checkAuthAndParseRequest()
  }, [authorizeRequest, navigate, t])

  const handleAuthorize = async () => {
    if (!authRequest) return
    await authorizeRequest(authRequest)
  }

  const handleDeny = () => {
    if (!authRequest) return

    // Redirect with access_denied error
    const errorUrl = buildErrorResponse(
      authRequest.redirect_uri,
      'access_denied',
      'User denied authorization',
      authRequest.state
    )

    window.location.href = errorUrl
  }

  if (isLoading) {
    return (
      <AuthSplitLayout>
        <div className="lp-state" role="status">
          <Spinner size="lg" />
          <p className="lp-state-msg">{t('authorize.authorizing')}</p>
        </div>
      </AuthSplitLayout>
    )
  }

  if (error && !authRequest) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop" role="alert">
          <div className="lp-badge lp-badge-brand">
            <CircleAlert size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('authorize.errorTitle')}</h2>
          <p className="lp-state-msg">{error}</p>
          <hr className="lp-state-divider" />
          <Button onClick={() => navigate('/login')} className="lp-btn-primary w-full">
            {t('backToLogin')}
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  if (!authRequest) {
    return null
  }

  const appName = getAppName(authRequest.client_id)
  const scopes = parseScopes(authRequest.scope)
  const scopeDescriptions = getScopeDescriptions(scopes)

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('authorize.title')}</h2>
        <p className="lp-subheading">{t('authorize.description', { app: appName })}</p>
      </div>

      <div className="lp-rise lp-rise-3">
        {error && (
          <Alert variant="error" className="lp-alert">
            {error}
          </Alert>
        )}

        {user && (
          <div className="lp-chip">
            <Avatar size="sm" fallback={user.name?.charAt(0) || user.email?.charAt(0) || 'U'} />
            <div>
              <div className="lp-chip-email">{user.name || t('authorize.defaultUser')}</div>
              <div className="lp-chip-label">{user.email}</div>
            </div>
          </div>
        )}

        <div className="lp-panel">
          <h3 className="lp-panel-title">{t('authorize.permissionsTitle')}</h3>
          <ul className="lp-panel-list">
            {scopeDescriptions.map(({ scope, description }) => (
              <li key={scope} className="lp-panel-item">
                <Check size={16} aria-hidden="true" />
                {description}
              </li>
            ))}
          </ul>
        </div>

        <div className="lp-panel">
          <h3 className="lp-panel-title">{t('authorize.secureTitle')}</h3>
          <p className="lp-panel-item">
            <Shield size={16} aria-hidden="true" />
            {t('authorize.secureDescription')}
          </p>
        </div>

        <hr className="lp-state-divider" />

        <div className="lp-form">
          <Button
            onClick={handleAuthorize}
            disabled={isAuthorizing}
            isLoading={isAuthorizing}
            className="lp-btn-primary w-full"
          >
            {isAuthorizing ? t('authorize.authorizing') : t('authorize.approve')}
            {!isAuthorizing && <ArrowRight size={16} aria-hidden="true" />}
          </Button>

          <Button
            onClick={handleDeny}
            disabled={isAuthorizing}
            variant="outline"
            className="lp-btn-social"
          >
            {t('authorize.deny')}
          </Button>
        </div>

        <p className="lp-support">
          {t('authorize.footerConsent', { app: appName })}
          <br />
          {t('authorize.footerRevoke')}
        </p>
      </div>
    </AuthSplitLayout>
  )
}
